// Polling service for real-time status updates with Supabase
// Uses getCalls() endpoint to poll for status changes since checkCallStatus is a webhook endpoint
import { CallRecord } from '../types';
import { api } from './api';
import config from '../config';

export interface PollingOptions {
  interval?: number;
  onUpdate?: (calls: CallRecord[]) => void;
  onError?: (error: Error) => void;
  maxRetries?: number;
}

export class PollingService {
  private intervalId: NodeJS.Timeout | null = null;
  private isActive = false;
  private options: PollingOptions;
  private retryCount = 0;
  private lastCallCount = 0;
  private lastStatusCheckAt: number | null = null;
  private minStatusCheckIntervalMs = 15000; // 15 seconds between status checks

  constructor(options: PollingOptions = {}) {
    this.options = {
      interval: config.pollingInterval,
      maxRetries: 3,
      ...options,
    };
  }

  start(): void {
    if (this.isActive) return;

    this.isActive = true;
    this.poll(); // Initial poll
    this.intervalId = setInterval(() => this.poll(), this.options.interval);
  }

  stop(): void {
    this.isActive = false;
    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }
  }

  isPolling(): boolean {
    return this.isActive;
  }

  private async poll(): Promise<void> {
    if (!this.isActive) return;

    try {
      const calls = await api.getCalls();
      const currentCallCount = calls.length;
      const hasProcessingCalls = calls.some(call => call.status === 'processing');

      // Throttle status checks to avoid rate limits
      const now = Date.now();
      const canCheck = hasProcessingCalls &&
        (this.lastStatusCheckAt === null || (now - this.lastStatusCheckAt) >= this.minStatusCheckIntervalMs);

      // Note: We don't need to call checkCallStatus() anymore since getCalls() already returns current statuses
      // The getCalls() function will show any status updates from webhooks
      if (canCheck) {
        console.log('🔍 Polling: Checking call statuses via getCalls()...')
        this.lastStatusCheckAt = Date.now();
        console.log('✅ Polling: Status check completed - using getCalls() to get current statuses')
      } else {
        console.log('⏰ Polling: Skipping status check (throttled or no processing calls)')
      }

      // Only trigger update if there are changes or processing calls
      if (currentCallCount !== this.lastCallCount || hasProcessingCalls) {
        this.options.onUpdate?.(calls);
        this.lastCallCount = currentCallCount;
        this.retryCount = 0; // Reset retry count on success
      }

      // If no processing calls, we can slow down polling
      if (!hasProcessingCalls) {
        // Optional: Increase interval when no active calls
        // this.adjustInterval();
      }

    } catch (error) {
      console.error('Polling error:', error);
      this.retryCount++;

      if (this.retryCount >= (this.options.maxRetries || 3)) {
        console.error('Max retries reached, stopping polling');
        this.stop();
        this.options.onError?.(error as Error);
      } else {
        // Exponential backoff for retries
        const backoffDelay = Math.min(1000 * Math.pow(2, this.retryCount), 30000);
        console.log(`Retrying in ${backoffDelay}ms...`);
        setTimeout(() => this.poll(), backoffDelay);
      }
    }
  }

  // Optional: Adjust polling interval based on activity
  private adjustInterval(): void {
    // If no processing calls, increase interval to reduce API calls
    // This is optional and can be enabled if you want to optimize costs
    if (this.options.interval !== undefined && this.options.interval < 30000) {
      this.options.interval = Math.min(this.options.interval * 1.5, 30000);
      if (this.intervalId) {
        clearInterval(this.intervalId);
        this.intervalId = setInterval(() => this.poll(), this.options.interval);
      }
    }
  }
}

// Hook for React components
export const usePollingService = (options: PollingOptions = {}) => {
  const pollingService = new PollingService(options);

  return {
    startPolling: () => pollingService.start(),
    stopPolling: () => pollingService.stop(),
    isPolling: () => pollingService.isPolling(),
  };
};
